import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { onboardingAPI } from '../../services/api';

const medicalHistorySchema = z.object({
  ailments: z.string().optional(),
  nailsBrittle: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  bowelMovements: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  urinatePerDay: z.enum(['1', '2', '3', '4', '5', '6+'], { required_error: 'Please select frequency' }),
  stressLevel: z.enum(['Very High', 'High', 'Moderate', 'Low', 'None'], { required_error: 'Please select stress level' }),
  dailyEnergy: z.enum(['Excellent', 'Good', 'Fair', 'Poor'], { required_error: 'Please select energy level' }),
  medications: z.string().optional(),
  sleepPerNight: z.enum(['1', '2', '3', '4', '5', '6', '7', '8+'], { required_error: 'Please select sleep hours' }),
  surgery: z.string().optional(),
  smoker: z.enum(['Yes', 'No'], { required_error: 'Please select an option' })
});

type MedicalHistoryFormData = z.infer<typeof medicalHistorySchema>;

interface MedicalHistoryFormProps {
  onComplete: () => void;
}

const MedicalHistoryForm: React.FC<MedicalHistoryFormProps> = ({ onComplete }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<MedicalHistoryFormData>({
    resolver: zodResolver(medicalHistorySchema)
  });

  const onSubmit = async (data: MedicalHistoryFormData) => {
    setIsSubmitting(true);
    try {
      await onboardingAPI.submitMedicalHistory(data);
      onComplete();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to submit medical history form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const RadioGroup = ({ name, question, error }: { name: keyof MedicalHistoryFormData; question: string; error?: string }) => (
    <div className="mb-6">
      <fieldset>
        <legend className="text-sm font-medium text-gray-900 mb-3">
          **{question}
        </legend>
        <div className="flex space-x-6">
          <label className="flex items-center">
            <input
              {...register(name)}
              type="radio"
              value="Yes"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">Yes</span>
          </label>
          <label className="flex items-center">
            <input
              {...register(name)}
              type="radio"
              value="No"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">No</span>
          </label>
        </div>
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      </fieldset>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white shadow rounded-lg p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Medical History</h1>
          <p className="text-red-600 font-medium">**FIELDS ARE REQUIRED</p>
          <p className="text-gray-600 mt-2">
            This information helps us understand your current health status and create a safe, personalized fitness program.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Ailments */}
          <div>
            <label htmlFor="ailments" className="block text-sm font-medium text-gray-700 mb-2">
              Current Ailments (Optional)
            </label>
            <textarea
              {...register('ailments')}
              id="ailments"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="List any current health conditions, injuries, or ailments..."
            />
          </div>

          {/* Health Indicators */}
          <RadioGroup
            name="nailsBrittle"
            question="Do you have brittle nails?"
            error={errors.nailsBrittle?.message}
          />

          <RadioGroup
            name="bowelMovements"
            question="Do you have regular bowel movements?"
            error={errors.bowelMovements?.message}
          />

          <div>
            <label htmlFor="urinatePerDay" className="block text-sm font-medium text-gray-700 mb-2">
              **How many times do you urinate per day?
            </label>
            <select
              {...register('urinatePerDay')}
              id="urinatePerDay"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.urinatePerDay ? 'border-red-300' : 'border-gray-300'
              }`}
              required
            >
              <option value="">Select frequency</option>
              <option value="1">1 time</option>
              <option value="2">2 times</option>
              <option value="3">3 times</option>
              <option value="4">4 times</option>
              <option value="5">5 times</option>
              <option value="6+">6+ times</option>
            </select>
            {errors.urinatePerDay && (
              <p className="mt-1 text-sm text-red-600">{errors.urinatePerDay.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="stressLevel" className="block text-sm font-medium text-gray-700 mb-2">
              **What is your current stress level?
            </label>
            <select
              {...register('stressLevel')}
              id="stressLevel"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.stressLevel ? 'border-red-300' : 'border-gray-300'
              }`}
              required
            >
              <option value="">Select stress level</option>
              <option value="Very High">Very High</option>
              <option value="High">High</option>
              <option value="Moderate">Moderate</option>
              <option value="Low">Low</option>
              <option value="None">None</option>
            </select>
            {errors.stressLevel && (
              <p className="mt-1 text-sm text-red-600">{errors.stressLevel.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="dailyEnergy" className="block text-sm font-medium text-gray-700 mb-2">
              **How would you rate your daily energy level?
            </label>
            <select
              {...register('dailyEnergy')}
              id="dailyEnergy"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.dailyEnergy ? 'border-red-300' : 'border-gray-300'
              }`}
              required
            >
              <option value="">Select energy level</option>
              <option value="Excellent">Excellent</option>
              <option value="Good">Good</option>
              <option value="Fair">Fair</option>
              <option value="Poor">Poor</option>
            </select>
            {errors.dailyEnergy && (
              <p className="mt-1 text-sm text-red-600">{errors.dailyEnergy.message}</p>
            )}
          </div>

          {/* Medications */}
          <div>
            <label htmlFor="medications" className="block text-sm font-medium text-gray-700 mb-2">
              Current Medications (Optional)
            </label>
            <textarea
              {...register('medications')}
              id="medications"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="List any prescription or over-the-counter medications you are currently taking..."
            />
          </div>

          <div>
            <label htmlFor="sleepPerNight" className="block text-sm font-medium text-gray-700 mb-2">
              **How many hours do you sleep per night?
            </label>
            <select
              {...register('sleepPerNight')}
              id="sleepPerNight"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.sleepPerNight ? 'border-red-300' : 'border-gray-300'
              }`}
              required
            >
              <option value="">Select sleep hours</option>
              <option value="1">1 hour</option>
              <option value="2">2 hours</option>
              <option value="3">3 hours</option>
              <option value="4">4 hours</option>
              <option value="5">5 hours</option>
              <option value="6">6 hours</option>
              <option value="7">7 hours</option>
              <option value="8+">8+ hours</option>
            </select>
            {errors.sleepPerNight && (
              <p className="mt-1 text-sm text-red-600">{errors.sleepPerNight.message}</p>
            )}
          </div>

          {/* Surgery */}
          <div>
            <label htmlFor="surgery" className="block text-sm font-medium text-gray-700 mb-2">
              Previous Surgeries (Optional)
            </label>
            <textarea
              {...register('surgery')}
              id="surgery"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="List any previous surgeries, procedures, or significant medical events..."
            />
          </div>

          <RadioGroup
            name="smoker"
            question="Are you a smoker?"
            error={errors.smoker?.message}
          />

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Privacy Notice:</h3>
            <p className="text-sm text-blue-700">
              Your medical information is kept confidential and is only used to create a safe and effective fitness program. 
              This information is not shared with third parties without your explicit consent.
            </p>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : 'Continue >'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MedicalHistoryForm;
